import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INQUIRY_SOURCE_LABELS, type CreateInquiryInput, type Inquiry, type InquiryFieldDefinition } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useInquiryTypes } from '../inquiryTypes';

/** Herkunft, die das Büro selbst erfassen kann. "website" kommt nur von der angebundenen Webseite. */
const MANUAL_SOURCES = ['phone', 'email', 'manual'] as const;
const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

type FieldValue = string | boolean | string[];

/** Eingabe eines Branchenfelds in die Form, die die API erwartet. Leere Werte fallen weg. */
function toApiValue(def: InquiryFieldDefinition, value: FieldValue | undefined): unknown {
  if (value === undefined || value === '' || value === false) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value : undefined;
  return typeof value === 'string' ? value.trim() || undefined : value;
}

export function NewInquiryPage() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const types = useInquiryTypes();

  const [source, setSource] = useState<(typeof MANUAL_SOURCES)[number]>('phone');
  const [typeKey, setTypeKey] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [customerType, setCustomerType] = useState<'' | 'private' | 'business'>('');
  const [preferredDate, setPreferredDate] = useState('');
  const [message, setMessage] = useState('');
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const type = types.get(typeKey) ?? types.types[0];
  const setValue = (key: string, value: FieldValue) => setValues((v) => ({ ...v, [key]: value }));

  const create = useMutation({
    mutationFn: (input: CreateInquiryInput) => api<{ data: Inquiry }>('POST', '/inquiries', input),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['inquiries'] });
      navigate(`/anfragen/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError(e.message);
      } else {
        setFormError(e instanceof ApiError ? e.message : 'Die Anfrage konnte nicht gespeichert werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!type) return;
    // Die Einwilligung wird nie stillschweigend gesetzt. Das Büro bestätigt sie ausdrücklich.
    if (!consent) {
      setErrors({ consent: 'Bitte bestätigen Sie, dass der Kunde der Speicherung seiner Angaben zugestimmt hat.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      return;
    }
    setErrors({});

    const fields: Record<string, unknown> = {};
    for (const def of type.fields) {
      const value = toApiValue(def, values[def.key]);
      if (value !== undefined) fields[def.key] = value;
    }
    const input = {
      type: type.key,
      source,
      contact: {
        name: name.trim(),
        email: email.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
        ...(customerType ? { customerType } : {}),
      },
      ...(message.trim() ? { message: message.trim() } : {}),
      ...(preferredDate ? { preferredDate } : {}),
      fields,
      consentAt: new Date().toISOString(),
    };
    create.mutate(input as CreateInquiryInput);
  }

  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const shownKeys = new Set(['contact.name', 'contact.email', 'contact.phone', 'contact.company', 'message', 'preferredDate', 'consent', ...(type?.fields.map((f) => `fields.${f.key}`) ?? [])]);
  const otherErrors = Object.entries(errors).filter(([key]) => !shownKeys.has(key));

  return (
    <>
      <p><Link to="/anfragen" className="back-link">← Alle Anfragen</Link></p>
      <div className="page-head">
        <h1>Neue Anfrage</h1>
      </div>
      <p className="muted">Für Anfragen, die per Telefon, E-Mail oder persönlich eingehen.</p>

      {!types.types.length && !types.isLoading ? (
        <div className="card narrow">
          <p>Für Ihr Unternehmen sind noch keine Anfragetypen eingerichtet. Bitte wenden Sie sich an Ihren Administrator.</p>
        </div>
      ) : (
        <form className="card" onSubmit={submit} noValidate>
          {formError && <div className="alert alert--error" role="alert">{formError}</div>}
          {otherErrors.length > 0 && (
            <div className="alert alert--error" role="alert">
              {otherErrors.map(([key, message]) => <div key={key}>{message}</div>)}
            </div>
          )}

          <h2>Eingang</h2>
          <div className="form-grid">
            <label className="field">
              <span>Herkunft</span>
              <select className="input" value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                {MANUAL_SOURCES.map((s) => <option key={s} value={s}>{upperFirst(INQUIRY_SOURCE_LABELS[s] ?? s)}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Art der Anfrage</span>
              <select className="input" value={type?.key ?? ''} onChange={(e) => { setTypeKey(e.target.value); setValues({}); setErrors({}); }}>
                {types.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              {error('type')}
            </label>
          </div>

          <h2>Kontakt</h2>
          <div className="form-grid">
            <label className="field">
              <span>Name <abbr title="Pflichtfeld">*</abbr></span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors['contact.name']} />
              {error('contact.name')}
            </label>
            <label className="field">
              <span>E-Mail <abbr title="Pflichtfeld">*</abbr></span>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors['contact.email']} />
              {error('contact.email')}
            </label>
            <label className="field">
              <span>Telefon</span>
              <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
              {error('contact.phone')}
            </label>
            <label className="field">
              <span>Firma</span>
              <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="off" />
              {error('contact.company')}
            </label>
            <label className="field">
              <span>Kundenart</span>
              <select className="input" value={customerType} onChange={(e) => setCustomerType(e.target.value as typeof customerType)}>
                <option value="">Nicht angegeben</option>
                <option value="private">Privat</option>
                <option value="business">Firma</option>
              </select>
              {error('contact.customerType')}
            </label>
            <label className="field">
              <span>Wunschtermin</span>
              <input className="input" type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} />
              {error('preferredDate')}
            </label>
          </div>

          {type && type.fields.length > 0 && <h2>{type.label}</h2>}
          <div className="form-grid">
            {type?.fields.map((def) => (
              <FieldInput key={`${type.key}:${def.key}`} def={def} value={values[def.key]} onChange={(v) => setValue(def.key, v)} error={errors[`fields.${def.key}`]} />
            ))}
          </div>

          <label className="field">
            <span>Nachricht oder Notizen zum Gespräch</span>
            <textarea className="input textarea" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} />
            {error('message')}
          </label>

          <h2>Einwilligung</h2>
          <label className="check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} aria-invalid={!!errors.consent} />
            <span>Der Kunde hat zugestimmt, dass seine Angaben zur Bearbeitung der Anfrage gespeichert werden (zum Beispiel mündlich am Telefon). <abbr title="Pflichtfeld">*</abbr></span>
          </label>
          {error('consent')}
          <p className="muted small">Mit dem Speichern wird der aktuelle Zeitpunkt als Zeitpunkt der Einwilligung festgehalten.</p>

          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={create.isPending || !type}>
              {create.isPending ? 'Wird gespeichert …' : 'Anfrage speichern'}
            </button>
            <Link className="btn btn--secondary" to="/anfragen">Abbrechen</Link>
          </div>
        </form>
      )}
    </>
  );
}

/** Eingabefeld passend zum Feldtyp aus der Definition des Anfragetyps */
function FieldInput({ def, value, onChange, error }: { def: InquiryFieldDefinition; value: FieldValue | undefined; onChange: (value: FieldValue) => void; error?: string }) {
  const label = (
    <span>
      {def.label}
      {def.unit ? ` (${def.unit})` : ''}
      {def.required ? <> <abbr title="Pflichtfeld">*</abbr></> : null}
    </span>
  );
  const message = error ? <small className="field__error" role="alert">{error}</small> : null;
  const text = typeof value === 'string' ? value : '';

  switch (def.type) {
    case 'textarea':
      return (
        <label className="field field--wide">
          {label}
          <textarea className="input textarea" value={text} onChange={(e) => onChange(e.target.value)} maxLength={5000} />
          {message}
        </label>
      );
    case 'select':
      return (
        <label className="field">
          {label}
          <select className="input" value={text} onChange={(e) => onChange(e.target.value)}>
            <option value="">Nicht angegeben</option>
            {def.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {message}
        </label>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="field field--wide">
          {label}
          <div className="checks">
            {def.options?.map((o) => (
              <label key={o.value} className="check">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...selected, o.value] : selected.filter((v) => v !== o.value))} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {message}
        </div>
      );
    }
    case 'boolean':
      return (
        <div className="field">
          {label}
          <label className="check">
            <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
            <span>Ja</span>
          </label>
          {message}
        </div>
      );
    default: {
      // Zahlen als Text, damit auch die Eingabe mit Komma möglich ist. Die API wandelt sie um und prüft sie.
      const inputType = def.type === 'date' ? 'date' : def.type === 'email' ? 'email' : def.type === 'phone' ? 'tel' : 'text';
      return (
        <label className="field">
          {label}
          <input className="input" type={inputType} inputMode={def.type === 'number' ? 'decimal' : undefined} value={text} onChange={(e) => onChange(e.target.value)} autoComplete="off" aria-invalid={!!error} />
          {message}
        </label>
      );
    }
  }
}
